import { VariantProps, cva } from 'class-variance-authority';
import { ChangeEvent, InputHTMLAttributes, forwardRef, useCallback } from 'react';
import { cn } from 'styles';

const inputVariants = cva('', {
  variants: {
    size: {
      small: 'h-8',
      medium: 'h-9',
      large: 'h-10',
    },

    focusStyle: {
      outline: 'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      border: 'focus-visible:border-primary',
      none: '',
    },
  },
});

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> &
  VariantProps<typeof inputVariants> & {
    onChangeDirect?: (v: string) => void;
  };

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      size = 'medium',
      focusStyle = 'outline',
      onChange,
      onChangeDirect,
      ...props
    },
    ref,
  ) => {
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange?.(event);
        onChangeDirect?.(event.target.value);
      },
      [onChange, onChangeDirect],
    );

    return (
      <input
        {...props}
        ref={ref}
        type={type}
        onChange={handleChange}
        className={cn(
          'flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background focus-visible:outline-none',
          'text-sm text-secondary-foreground phone:text-base',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'placeholder:text-xs placeholder:italic placeholder:text-muted-foreground/60',
          'file:border-0 file:bg-transparent file:text-sm file:font-medium',
          inputVariants({ className, size, focusStyle }),
        )}
      />
    );
  },
);

Input.displayName = 'Input';
